// src/components/accordion/ctl-accordion.js
//
// Accessible accordion (single or multiple open).
//
// Attributes (ctl-accordion):
// - multiple (boolean) : allow multiple panels open (default false)
// - value (string)     : active item id (single mode). In multiple mode, use "values".
// - values (json)      : JSON array of open item ids (multiple mode) e.g. '["a","b"]'
// - disabled (boolean)
//
// Properties (optional, but supported):
// - items (array) : [{ id, title, disabled?, open?, content? }]  (content is optional string HTML)
//
// Events:
// - ctl-change (detail: { value, values, id, open })
//
// Slots:
// - default: <ctl-accordion-item> children
//
// Subcomponent: <ctl-accordion-item>
// - attributes: id (required), title, open, disabled
// - slots: default (panel content), title (custom title content), actions (right-side actions in header)
//
// A11y:
// - button role for header; aria-expanded/aria-controls; panel has role="region" and aria-labelledby
// - roving focus with ArrowUp/ArrowDown, Home/End; Enter/Space toggles.

import "../foundation/ctl-icon.js";
import "../foundation/ctl-divider.js";

const ACC_TEMPLATE = document.createElement("template");
ACC_TEMPLATE.innerHTML = `
<style>
  :host{
    --font: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    --text: var(--ctl-color-text);
    --muted: var(--ctl-color-muted);
    --surface: var(--ctl-color-surface);
    --surface-2: var(--ctl-color-surface-2);
    --border: var(--ctl-color-border);
    --brand: var(--ctl-color-brand);

    --radius: var(--ctl-radius-lg);
    --focus: var(--ctl-focus-ring);

    display:block;
    font-family: var(--font);
    color: var(--text);
  }

  .frame{
    border: var(--ctl-border-width-thin) solid var(--border);
    border-radius: var(--radius);
    background: var(--surface);
    overflow:hidden;
  }

  :host([disabled]){
    opacity: var(--ctl-opacity-disabled);
    pointer-events:none;
  }

  /* hide templated light-dom artifacts */
  ::slotted(template){ display:none; }
</style>

<div class="frame" part="frame">
  <slot></slot>
</div>
`;

const ITEM_TEMPLATE = document.createElement("template");
ITEM_TEMPLATE.innerHTML = `
<style>
  :host{
    display:block;
    border-top: var(--ctl-border-width-thin) solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
  }
  :host(:first-child){
    border-top: 0;
  }

  .head{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2);
    padding: var(--ctl-space-3);
    background: var(--ctl-color-surface);
  }

  button{
    all: unset;
    cursor: pointer;
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2);
    flex: 1 1 auto;
    min-width: 0;
    border-radius: var(--ctl-radius-md);
    padding: var(--ctl-space-2) var(--ctl-space-3);
    margin: calc(-1 * var(--ctl-space-2)) calc(-1 * var(--ctl-space-3));
  }

  button:focus-visible{
    outline:none;
    box-shadow: var(--ctl-focus-ring);
  }

  :host([disabled]) button{
    cursor: not-allowed;
    opacity: var(--ctl-opacity-disabled);
  }

  .titleRow{
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap: var(--ctl-space-3);
    width: 100%;
    min-width: 0;
  }

  .title{
    font-weight: var(--ctl-font-weight-black);
    letter-spacing: -0.01em;
    line-height: 1.15;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .meta{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2);
    flex: 0 0 auto;
  }

  .chev{
    width: var(--ctl-space-4-5);
    height: var(--ctl-space-4-5);
    color: var(--ctl-color-muted);
    transition: transform .15s ease;
    flex: 0 0 auto;
  }
  :host([open]) .chev{ transform: rotate(180deg); }

  .panel{
    display:none;
    padding: 0 var(--ctl-space-3) var(--ctl-space-3);
    color: var(--ctl-color-text);
  }
  :host([open]) .panel{ display:block; }

  .panelInner{
    border-radius: var(--ctl-radius-md);
    background: var(--ctl-color-surface-2);
    border: var(--ctl-border-width-thin) solid color-mix(in srgb, var(--ctl-color-border) 80%, transparent);
    padding: var(--ctl-space-3);
  }

  .panelInner:empty{
    display:none;
  }

  ::slotted([slot="actions"]){
    display:inline-flex;
    align-items:center;
    gap: var(--ctl-space-2);
  }
</style>

<div class="head" part="header">
  <button id="btn" type="button" part="button">
    <span class="chev" aria-hidden="true">${chevronSvg()}</span>
    <span class="titleRow">
      <span class="title" part="title">
        <slot name="title" id="titleSlot"></slot>
        <span id="titleText"></span>
      </span>
      <span class="meta" part="meta">
        <slot name="actions"></slot>
      </span>
    </span>
  </button>
</div>

<div class="panel" id="panel" part="panel" role="region">
  <div class="panelInner" part="panel-inner">
    <slot></slot>
  </div>
</div>
`;

function chevronSvg() {
  return `
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;
}

function safeJsonParse(str, fallback) {
  try { return JSON.parse(str); } catch { return fallback; }
}

function toArray(v) {
  return Array.isArray(v) ? v : [];
}

function clampStr(v){ return String(v ?? "").trim(); }

class CtlAccordionItem extends HTMLElement {
  static get observedAttributes() {
    return ["open", "disabled", "title", "id"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(ITEM_TEMPLATE.content.cloneNode(true));
    this._btn = this.shadowRoot.getElementById("btn");
    this._panel = this.shadowRoot.getElementById("panel");
    this._titleText = this.shadowRoot.getElementById("titleText");
    this._titleSlot = this.shadowRoot.getElementById("titleSlot");

    this._onClick = this._onClick.bind(this);
    this._onSlotChange = this._onSlotChange.bind(this);
  }

  connectedCallback() {
    if (!this.id) this.id = `acc_${Math.random().toString(16).slice(2)}`;
    this._btn.addEventListener("click", this._onClick);
    this._titleSlot.addEventListener("slotchange", this._onSlotChange);
    this._syncA11y();
    this._syncTitle();
  }

  disconnectedCallback() {
    this._btn.removeEventListener("click", this._onClick);
    this._titleSlot.removeEventListener("slotchange", this._onSlotChange);
  }

  attributeChangedCallback() {
    this._syncA11y();
    this._syncTitle();
  }

  get open(){ return this.hasAttribute("open"); }
  set open(v){ v ? this.setAttribute("open","") : this.removeAttribute("open"); }

  get disabled(){ return this.hasAttribute("disabled"); }
  set disabled(v){ v ? this.setAttribute("disabled","") : this.removeAttribute("disabled"); }

  get title(){ return this.getAttribute("title") || ""; }
  set title(v){ this.setAttribute("title", String(v ?? "")); }

  _syncTitle() {
    // If slot has content, we keep titleText empty.
    const slotted = this._titleSlot.assignedNodes({ flatten: true }) || [];
    const hasSlotContent = slotted.some((n) => (n.nodeType === Node.ELEMENT_NODE) || (n.nodeType === Node.TEXT_NODE && String(n.textContent||"").trim()));
    this._titleText.textContent = hasSlotContent ? "" : clampStr(this.title);
  }

  _syncA11y() {
    const panelId = `${this.id}__panel`;
    const btnId = `${this.id}__btn`;

    this._btn.id = btnId;
    this._panel.id = panelId;

    this._btn.setAttribute("aria-controls", panelId);
    this._btn.setAttribute("aria-expanded", this.open ? "true" : "false");
    this._btn.setAttribute("aria-disabled", this.disabled ? "true" : "false");
    this._btn.toggleAttribute("disabled", this.disabled);

    this._panel.setAttribute("aria-labelledby", btnId);
  }

  _onSlotChange() {
    this._syncTitle();
  }

  _onClick(e) {
    e.preventDefault();
    if (this.disabled) return;

    const nextOpen = !this.open;
    // Emit a request to parent; parent decides single/multiple rules.
    this.dispatchEvent(new CustomEvent("ctl-toggle", {
      bubbles: true,
      composed: true,
      detail: { id: this.id, open: nextOpen, item: this }
    }));
  }
}

export class CtlAccordion extends HTMLElement {
  static get observedAttributes() {
    return ["multiple", "value", "values", "disabled"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(ACC_TEMPLATE.content.cloneNode(true));
    this._onToggle = this._onToggle.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);

    this._value = null;
    this._values = [];
  }

  connectedCallback() {
    // hydrate attributes
    if (this.hasAttribute("value")) this._value = clampStr(this.getAttribute("value")) || null;
    if (this.hasAttribute("values")) this._values = toArray(safeJsonParse(this.getAttribute("values"), [])).map(String);

    this.addEventListener("ctl-toggle", this._onToggle);
    this.addEventListener("keydown", this._onKeyDown);

    // support data-driven items if set as property before connect
    if (Array.isArray(this._itemsPending)) {
      this.items = this._itemsPending;
      this._itemsPending = null;
    }

    // Defer _applyState() to allow child items to be parsed and connected first
    queueMicrotask(() => {
      if (this.isConnected) this._applyState();
    });
  }

  disconnectedCallback() {
    this.removeEventListener("ctl-toggle", this._onToggle);
    this.removeEventListener("keydown", this._onKeyDown);
  }

  attributeChangedCallback(name) {
    if (name === "value") this._value = clampStr(this.getAttribute("value")) || null;
    if (name === "values") this._values = toArray(safeJsonParse(this.getAttribute("values"), [])).map(String);
    this._applyState();
  }

  get multiple(){ return this.hasAttribute("multiple"); }
  set multiple(v){ v ? this.setAttribute("multiple","") : this.removeAttribute("multiple"); }

  get disabled(){ return this.hasAttribute("disabled"); }
  set disabled(v){ v ? this.setAttribute("disabled","") : this.removeAttribute("disabled"); }

  get value(){ return this.getAttribute("value") || ""; }
  set value(v){ v == null ? this.removeAttribute("value") : this.setAttribute("value", String(v)); }

  get values(){
    const raw = this.getAttribute("values");
    return raw ? toArray(safeJsonParse(raw, [])).map(String) : [];
  }
  set values(v){
    const arr = toArray(v).map(String);
    this.setAttribute("values", JSON.stringify(arr));
  }

  // Optional property-driven API
  get items(){ return this._items || []; }
  set items(v) {
    if (!this.isConnected) {
      this._itemsPending = v;
      return;
    }
    const items = toArray(v).map((x) => ({
      id: clampStr(x?.id),
      title: clampStr(x?.title),
      disabled: !!x?.disabled,
      open: !!x?.open,
      content: x?.content != null ? String(x.content) : null
    })).filter((x) => x.id);

    this._items = items;

    // Render items into light DOM for maximum slot compatibility.
    this.innerHTML = "";
    for (const it of items) {
      const el = document.createElement("ctl-accordion-item");
      el.id = it.id;
      el.setAttribute("title", it.title || it.id);
      if (it.disabled) el.setAttribute("disabled", "");
      if (it.open) el.setAttribute("open", "");
      if (it.content != null) el.innerHTML = it.content;
      this.appendChild(el);
    }

    // Initialize state from items open flags
    if (this.multiple) {
      const opens = items.filter((x) => x.open).map((x) => x.id);
      this.values = opens;
    } else {
      const first = items.find((x) => x.open);
      if (first) this.value = first.id;
    }

    this._applyState();
  }

  _itemsList() {
    return Array.from(this.querySelectorAll("ctl-accordion-item"));
  }

  _applyState() {
    const items = this._itemsList();
    if (items.length === 0) return;

    const isMultiple = this.multiple;
    const activeId = clampStr(this._value) || null;
    const openIds = new Set(toArray(this._values).map(String));

    for (const it of items) {
      // Disabled state bubbles from accordion disabled
      if (this.disabled) it.setAttribute("disabled", "");
      // If accordion isn't disabled, don't override item disabled state.

      // Manipulate the "open" attribute directly instead of using the .open property setter.
      // This avoids issues if items are not yet upgraded custom elements (expando properties
      // can shadow the eventual accessor) and keeps state in sync via attributes.
      const shouldBeOpen = isMultiple ? openIds.has(it.id) : (activeId === it.id);
      if (shouldBeOpen) {
        it.setAttribute("open", "");
      } else {
        it.removeAttribute("open");
      }

      // Roving tabindex managed at accordion level (buttons live in item shadow)
    }

    // Default roving tabindex: first enabled header button gets focusable
    queueMicrotask(() => this._syncRovingTabindex());
  }

  _syncRovingTabindex() {
    const items = this._itemsList();
    const btns = items.map((it) => it.shadowRoot?.getElementById("btn")).filter(Boolean);
    if (btns.length === 0) return;

    // Determine current focusable button: if one already tabindex=0, keep it.
    let idx = btns.findIndex((b) => b.tabIndex === 0);
    if (idx < 0) idx = btns.findIndex((b) => b.getAttribute("aria-disabled") !== "true");
    if (idx < 0) idx = 0;

    btns.forEach((b, i) => (b.tabIndex = (i === idx ? 0 : -1)));
  }

  _emitChange({ id, open }) {
    const detail = this.multiple
      ? { value: null, values: this.values, id, open }
      : { value: this.value || null, values: [], id, open };

    this.dispatchEvent(new CustomEvent("ctl-change", {
      bubbles: true,
      composed: true,
      detail
    }));
  }

  _onToggle(e) {
    // Event originates from item
    const { id, open } = e.detail || {};
    if (!id) return;

    const items = this._itemsList();
    const item = items.find((x) => x.id === id);
    if (!item || item.disabled || this.disabled) return;

    if (this.multiple) {
      const set = new Set(this.values);
      if (open) set.add(id);
      else set.delete(id);
      this.values = Array.from(set);
      this._applyState();
      this._emitChange({ id, open });
      return;
    }

    // Single mode: if opening, set value; if closing, clear
    this.value = open ? id : "";
    this._applyState();
    this._emitChange({ id, open });
  }

  _onKeyDown(e) {
    // Handle roving focus among header buttons
    const key = e.key;
    if (!["ArrowDown","ArrowUp","Home","End","Enter"," "].includes(key)) return;

    const items = this._itemsList();
    const btns = items.map((it) => it.shadowRoot?.getElementById("btn")).filter(Boolean);
    if (btns.length === 0) return;

    const active = this.getRootNode().activeElement;
    let idx = btns.indexOf(active);

    // If focus is inside a button (shadow DOM), activeElement might be the host.
    // In practice, the activeElement should be the button since it is focusable.
    if (idx < 0) idx = btns.findIndex((b) => b.tabIndex === 0);
    if (idx < 0) idx = 0;

    const isDisabledBtn = (b) => b.getAttribute("aria-disabled") === "true";

    const move = (nextIdx) => {
      // Skip disabled
      let i = nextIdx;
      let guard = 0;
      while (guard++ < btns.length && isDisabledBtn(btns[i])) {
        i = (i + 1) % btns.length;
      }
      btns.forEach((b, j) => (b.tabIndex = j === i ? 0 : -1));
      btns[i].focus();
    };

    if (key === "ArrowDown") {
      e.preventDefault();
      move((idx + 1) % btns.length);
      return;
    }
    if (key === "ArrowUp") {
      e.preventDefault();
      move((idx - 1 + btns.length) % btns.length);
      return;
    }
    if (key === "Home") {
      e.preventDefault();
      move(0);
      return;
    }
    if (key === "End") {
      e.preventDefault();
      move(btns.length - 1);
      return;
    }

    if (key === "Enter" || key === " ") {
      // Toggle focused item
      const it = items[idx];
      if (!it) return;
      e.preventDefault();
      it.dispatchEvent(new CustomEvent("ctl-toggle", {
        bubbles: true,
        composed: true,
        detail: { id: it.id, open: !it.open, item: it }
      }));
    }
  }
}

// Register custom elements
if (!customElements.get("ctl-accordion")) {
  customElements.define("ctl-accordion", CtlAccordion);
}

if (!customElements.get("ctl-accordion-item")) {
  customElements.define("ctl-accordion-item", CtlAccordionItem);
}
